import { useCallback } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { codeAppliedState, paymentModalOpenState, planState, showAppSubscriberModalState, subscriptionState, userDataState } from "@/lib/store";
import { restartSubscription } from "@/controllers/subscription";
import { effectiveCheckoutParams } from "@/lib/checkoutIntent";
import useOpenSubscriptionForm from "./useOpenSubscriptionForm";
import { User } from "@/models/auth";

interface AppSubscriberUpgradeOptions {
    openPaymentModal?: boolean;
    openForm?: boolean;
    closeModalAfterDelay?: boolean;
    user?: User;
}

const useAppSubscriberUpgrade = () => {
    const router = useRouter();
    const userData = useAtomValue(userDataState);
    const setPaymentModalOpen = useSetAtom(paymentModalOpenState);
    const setSubscription = useSetAtom(subscriptionState);
    const setPlan = useSetAtom(planState);
    const setCodeApplied = useSetAtom(codeAppliedState);
    const setShowAppSubscriberModal = useSetAtom(showAppSubscriberModalState);
    const { openSubscriptionForm } = useOpenSubscriptionForm();

    const handleAppSubscriberUpgrade = useCallback(
        (options: AppSubscriberUpgradeOptions = {}) => {
            const { openPaymentModal = true, openForm = true, closeModalAfterDelay = true, user } = options;

            const effT = effectiveCheckoutParams(router.query).t;
            const interval = effT === "monthly" || (router.query.pt as string) === "m" ? "monthly" : "annually";
            const targetUser = user || userData;

            if (openPaymentModal) {
                setPaymentModalOpen(true);
            }

            // For app subscribers, handle the subscription directly with APPDISC code
            // Don't call openSubscriptionForm to avoid duplicate API calls
            if (targetUser?.app_subscriber_only) {
                restartSubscription("APPDISC")
                    .then((res) => {
                        if (!res || !Array.isArray(res.plans)) return;
                        setCodeApplied("APPDISC");
                        setSubscription(res);
                        setPlan(res.plans.find((plan) => plan.product_name.includes("Advanced") && plan.plan_interval === interval));
                    })
                    .then(() => {
                        setShowAppSubscriberModal(true);
                        if (closeModalAfterDelay) {
                            setTimeout(() => {
                                setPaymentModalOpen(false);
                            }, 1500);
                        }
                    })
                    .catch((error) => {
                        console.error("Error during app subscriber upgrade:", error);
                    });
            } else if (openForm) {
                // For non-app subscribers, use the regular flow
                openSubscriptionForm();
            }
        },
        [router.query.pt, userData, setPaymentModalOpen, openSubscriptionForm, setCodeApplied, setSubscription, setPlan, setShowAppSubscriberModal]
    );

    return { handleAppSubscriberUpgrade };
};

export default useAppSubscriberUpgrade;
